/** ONE SECURITY CENTRE — real backend-enforced controls dashboard. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Lock } from "lucide-react";
import { Modal, Field } from "../operations/StockInventoryDialogs";
import { SensitiveWatermark } from "@/components/app/SensitiveWatermark";
import { DomainHealthSection } from "./PublicReferenceSources";

const TABS = ["OVERVIEW", "USERS", "ROLES & ACCESS", "ACTIVE SESSIONS", "TRUSTED DEVICES",
  "AUTHENTICATION", "SENSITIVE ACCESS", "SECURITY EVENTS", "DOWNLOADS & EXPORTS",
  "INTEGRATIONS", "DOMAIN & EMAIL HEALTH", "SECURITY POLICIES", "ACCESS REVIEWS", "ALERTS", "AUDIT"];
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-").replace(/(^-|-$)/g, "");
const inputCls = "text-[11px] border border-slate-300 rounded-md px-2 py-1.5";
const SEV = { HIGH: "bg-rose-100 text-rose-800", MEDIUM: "bg-amber-100 text-amber-800",
  WARNING: "bg-amber-100 text-amber-800", INFO: "bg-slate-100 text-slate-600" };
const Pill = ({ v, map }) => <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded " +
  ((map || SEV)[v] || "bg-slate-100 text-slate-600")}>{(v || "").replace(/_/g, " ")}</span>;
const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 16);

function Tbl({ cols, rows, empty, testid }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-x-auto" data-testid={testid}>
      <table className="w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500"><tr>
          {cols.map((c) => <th key={c} className="text-left px-3 py-2">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={cols.length}
            className="text-center text-slate-400 px-3 py-8">{empty || "Nothing here."}</td></tr>}
          {rows.map((r, i) => <tr key={i} className="border-t border-slate-100">
            {r.map((v, j) => <td key={j} className="px-3 py-1.5">{(v === null || v === undefined || v === "") ? "—" : v}</td>)}</tr>)}
        </tbody>
      </table>
    </div>);
}

/** Wraps a mutating call: on 403 STEP_UP_REQUIRED shows password modal, then retries. */
export function useStepUp() {
  const [pending, setPending] = useState(null);
  const run = useCallback(async (fn) => {
    try { return await fn(); }
    catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "STEP_UP_REQUIRED") setPending({ fn, area: d.area });
      else throw e;
    }
  }, []);
  const modal = pending && (
    <StepUpModal area={pending.area}
        onClose={() => setPending(null)}
        onVerified={async () => { const f = pending.fn; setPending(null); await f(); }} />);
  return { run, modal };
}

function StepUpModal({ area, onClose, onVerified }) {
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/security-centre/step-up", { password: pwd });
      toast.success("Identity verified");
      await onVerified();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    setBusy(false);
  };
  return (
    <Modal title="Step-up verification required" onClose={onClose}>
      <div className="space-y-3" data-testid="stepup-modal">
        <div className="text-[11.5px] text-slate-600 flex items-center gap-2">
          <Lock className="h-4 w-4 text-amber-600" />
          The area <b>{area?.replace(/_/g, " ")}</b> requires re-verification of your identity.</div>
        <Field label="Current password" required>
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
              data-testid="stepup-password" className={inputCls + " w-full mt-1"} /></Field>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={verify} disabled={busy || !pwd} data-testid="stepup-verify"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            Verify & continue</button>
        </div>
      </div>
    </Modal>);
}

export default function SecurityCentre() {
  const [allowed, setAllowed] = useState(null);
  const [tab, setTab] = useState("OVERVIEW");
  const [k, setK] = useState(0);
  const bump = () => setK((x) => x + 1);
  const { run, modal } = useStepUp();
  useEffect(() => {
    api.get("/security-centre/access").then(({ data }) => setAllowed(data.allowed))
      .catch(() => setAllowed(false));
  }, []);
  if (allowed === false) return (
    <div className="max-w-md mx-auto mt-20 text-center space-y-3" data-testid="sc-denied">
      <ShieldAlert className="h-10 w-10 mx-auto text-slate-300" />
      <div className="text-sm text-slate-500">Security Centre is restricted to security administrators.</div>
    </div>);
  if (!allowed) return null;
  const P = { refreshKey: k, bump, run };
  return (
    <div className="max-w-[1500px] mx-auto space-y-4 relative" data-testid="security-centre-page">
      <SensitiveWatermark area="SECURITY_CENTRE" />
      <header className="flex flex-wrap items-center gap-3 bg-slate-950 text-white rounded-xl px-4 py-3">
        <div className="h-10 w-10 rounded-lg bg-rose-500 text-white grid place-items-center">
          <ShieldCheck className="h-5 w-5" /></div>
        <div className="flex-1">
          <div className="text-lg font-heading font-semibold tracking-wide">ONE SECURITY CENTRE</div>
          <div className="text-[10.5px] text-slate-400">Zero-trust access layer — all controls enforced server-side</div>
        </div>
        <Readiness refreshKey={k} />
      </header>
      <div className="flex gap-4 items-start">
        <nav className="w-48 shrink-0 space-y-0.5" data-testid="sc-nav">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`sc-nav-${slug(t)}`}
                className={"w-full text-left text-[11px] px-2.5 py-2 rounded-md " +
                  (tab === t ? "bg-slate-950 text-white font-semibold" : "text-slate-600 hover:bg-slate-100")}>
              {t}</button>))}
        </nav>
        <div className="flex-1 min-w-0">
          {tab === "OVERVIEW" && <OverviewTab {...P} goto={setTab} />}
          {tab === "USERS" && <UsersTab {...P} />}
          {tab === "ROLES & ACCESS" && <PrivilegedTab {...P} />}
          {tab === "ACTIVE SESSIONS" && <SessionsTab {...P} />}
          {tab === "TRUSTED DEVICES" && <DevicesTab {...P} />}
          {tab === "AUTHENTICATION" && <AuthTab {...P} />}
          {tab === "SENSITIVE ACCESS" && <SensitiveTab {...P} />}
          {tab === "SECURITY EVENTS" && <EventsTab {...P} />}
          {tab === "DOWNLOADS & EXPORTS" && <DownloadsTab {...P} />}
          {tab === "INTEGRATIONS" && <IntegrationsTab {...P} />}
          {tab === "DOMAIN & EMAIL HEALTH" && <DomainHealthSection />}
          {tab === "SECURITY POLICIES" && <PoliciesTab {...P} />}
          {tab === "ACCESS REVIEWS" && <ReviewsTab {...P} />}
          {tab === "ALERTS" && <AlertsTab {...P} />}
          {tab === "AUDIT" && <EventsTab {...P} auditMode />}
        </div>
      </div>
      {modal}
    </div>);
}

function Readiness({ refreshKey }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/security-centre/readiness").then(({ data }) => setD(data)); }, [refreshKey]);
  if (!d) return null;
  return (<>
    <button onClick={() => setOpen(true)} data-testid="sc-readiness-btn"
        className={"text-[11px] px-3 py-1.5 rounded-md font-semibold " +
          (d.overall === "SECURITY READY" ? "bg-emerald-500 text-white" : "bg-amber-400 text-slate-900")}>
      {d.overall}</button>
    {open && <Modal title="Security readiness (transparent — no opaque score)" onClose={() => setOpen(false)} wide>
      <div className="space-y-1.5" data-testid="sc-readiness">
        {d.checks.map((c) => (
          <div key={c.name} className="flex items-center gap-2 text-[11.5px] border border-slate-100 rounded-lg px-3 py-2">
            <span className="flex-1 font-medium">{c.name}
              <div className="text-[9.5px] text-slate-500 font-normal">{c.detail}</div></span>
            <Pill v={c.status} map={{ PASS: "bg-emerald-100 text-emerald-800",
              ACTION_REQUIRED: "bg-rose-100 text-rose-800", NOT_CONFIGURED: "bg-slate-200 text-slate-600" }} />
          </div>))}
      </div>
    </Modal>}
  </>);
}

function OverviewTab({ refreshKey, goto }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/security-centre/overview").then(({ data }) => setD(data)); }, [refreshKey]);
  if (!d) return null;
  return (
    <div className="space-y-4" data-testid="sc-overview">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {d.metrics.map((m) => (
          <button key={m.key} onClick={() => goto(m.tab)} data-testid={`sc-metric-${m.key}`}
              className="border border-slate-200 rounded-xl bg-white p-3 text-left hover:border-rose-500 transition-colors">
            <div className="text-2xl font-mono font-semibold">{m.value}</div>
            <div className="text-[9.5px] uppercase tracking-wide text-slate-600">{m.label}</div>
          </button>))}
      </div>
      <div className="border border-slate-200 rounded-xl bg-white p-4">
        <div className="text-[11px] font-semibold mb-2">SYSTEM PROTECTED — cannot be disabled by configuration or Admin Mode</div>
        <div className="flex flex-wrap gap-1.5" data-testid="sc-system-protected">
          {d.system_protected.map((s) => (
            <span key={s} className="text-[10px] px-2 py-1 rounded-full bg-slate-950 text-white inline-flex items-center gap-1">
              <Lock className="h-2.5 w-2.5" /> {s}</span>))}
        </div>
      </div>
    </div>);
}

function UsersTab({ refreshKey, bump, run }) {
  const [items, setItems] = useState([]);
  const [dormantOnly, setDormantOnly] = useState(false);
  useEffect(() => { api.get("/security-centre/users").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  const act = (u, action) => run(async () => {
    await api.post(`/security-centre/users/${u.id}/dormant-action`, { action });
    toast.success(`${action.replace(/_/g, " ")} — ${u.email}`); bump();
  });
  const rows = (dormantOnly ? items.filter((u) => u.dormant) : items).map((u) => [
    <span data-testid={`sc-user-${u.email}`}><b>{u.email}</b>
      {u.dormant && <span className="ml-1 text-[8.5px] px-1 rounded bg-orange-100 text-orange-800">DORMANT</span>}</span>,
    u.role, <Pill v={u.status} map={{ ACTIVE: "bg-emerald-100 text-emerald-800",
      INACTIVE: "bg-slate-200 text-slate-600", LOCKED: "bg-rose-100 text-rose-800",
      REVIEW_REQUIRED: "bg-amber-100 text-amber-800" }} />,
    fmt(u.last_login), u.mfa ? "MFA ✓" : "Password only", u.trusted_devices, u.sessions, u.failed_7d,
    <span className="space-x-1 whitespace-nowrap">
      <button onClick={() => act(u, "KEEP_ACTIVE")} className="text-[9.5px] px-1.5 py-0.5 rounded border border-emerald-300 text-emerald-700">Keep</button>
      <button onClick={() => act(u, "REVIEW_REQUIRED")} className="text-[9.5px] px-1.5 py-0.5 rounded border border-amber-300 text-amber-700">Review</button>
      <button onClick={() => act(u, "DEACTIVATE")} data-testid={`sc-deactivate-${u.email}`}
          className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700">Deactivate</button>
    </span>]);
  return (
    <div className="space-y-3" data-testid="sc-users">
      <label className="text-[11px] inline-flex items-center gap-1.5">
        <input type="checkbox" checked={dormantOnly} onChange={(e) => setDormantOnly(e.target.checked)}
            data-testid="sc-dormant-filter" /> Dormant account review only</label>
      <Tbl cols={["User", "Role", "Status", "Last login", "Auth", "Devices", "Sessions", "Failed 7d", "Dormant action"]}
          rows={rows} testid="sc-users-table" />
    </div>);
}

function PrivilegedTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/privileged").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  return <Tbl testid="sc-privileged" cols={["User", "Role", "Privileged permissions", "Flags"]}
      rows={items.map((u) => [u.email, u.role,
        <span className="flex flex-wrap gap-1">{u.permissions.map((p) =>
          <span key={p} className="text-[8.5px] px-1.5 py-0.5 rounded bg-slate-900 text-white">{p}</span>)}</span>,
        <span className="flex flex-wrap gap-1">{u.flags.map((f) =>
          <span key={f} className="text-[8.5px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{f.replace(/_/g, " ")}</span>)}</span>])} />;
}

function SessionsTab({ refreshKey, bump }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/sessions").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  const revoke = async (s) => {
    try { await api.post(`/security-centre/sessions/${s.session_ref}/revoke`);
      toast.success("Session revoked"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revokeAll = async (s) => {
    if (!window.confirm(`Revoke ALL sessions of ${s.email}?`)) return;
    try { const { data } = await api.post(`/security-centre/users/${s.user_id}/revoke-sessions`);
      toast.success(`${data.revoked} session(s) revoked`); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return <Tbl testid="sc-sessions" cols={["User", "Role", "Device", "IP", "Auth", "Login", "Last activity", ""]}
      rows={items.map((s) => [s.email, s.role, `${s.browser} / ${s.platform}`, s.ip,
        s.auth_method, fmt(s.created_at), fmt(s.last_seen),
        <span className="space-x-1 whitespace-nowrap">
          <button onClick={() => revoke(s)} data-testid={`sc-revoke-${s.session_ref}`}
              className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700">Revoke</button>
          <button onClick={() => revokeAll(s)} className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-400 text-rose-800">All user</button>
        </span>])} />;
}

function DevicesTab({ refreshKey, bump }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/devices").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  const set = async (d, status) => {
    try { await api.post(`/security-centre/devices/${d.id}/status`, { status });
      toast.success(`Device ${status.toLowerCase()}`); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return <Tbl testid="sc-devices" cols={["User", "Device", "First seen", "Last seen", "Trust", ""]}
      rows={items.map((d) => [d.email, d.name, fmt(d.first_seen), fmt(d.last_seen),
        <Pill v={d.status} map={{ TRUSTED: "bg-emerald-100 text-emerald-800",
          UNTRUSTED: "bg-amber-100 text-amber-800", REVIEW_REQUIRED: "bg-orange-100 text-orange-800",
          REVOKED: "bg-rose-100 text-rose-800" }} />,
        <span className="space-x-1 whitespace-nowrap">
          {d.status !== "TRUSTED" && <button onClick={() => set(d, "TRUSTED")} data-testid={`sc-trust-${d.id}`}
              className="text-[9.5px] px-1.5 py-0.5 rounded border border-emerald-300 text-emerald-700">Mark trusted</button>}
          {d.status !== "REVOKED" && <button onClick={() => set(d, "REVOKED")} data-testid={`sc-revoke-dev-${d.id}`}
              className="text-[9.5px] px-1.5 py-0.5 rounded border border-rose-300 text-rose-700">Revoke trust</button>}
        </span>])} />;
}

function AuthTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/users").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  return (
    <div className="space-y-2" data-testid="sc-auth">
      <div className="text-[11px] text-slate-500">Passkey preferred where supported. TOTP MFA available today; no biometric data is ever stored.</div>
      <Tbl cols={["User", "MFA / Passkey", "Last authentication", "Methods"]}
          rows={items.map((u) => [u.email,
            u.mfa ? <Pill v="CONFIGURED" map={{ CONFIGURED: "bg-emerald-100 text-emerald-800" }} />
                  : <Pill v="PASSWORD_ONLY" map={{ PASSWORD_ONLY: "bg-amber-100 text-amber-800" }} />,
            fmt(u.last_login), u.mfa ? "Password + TOTP" : "Password"])} />
    </div>);
}

function SensitiveTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/security-centre/alerts").then(({ data }) =>
      setItems((data.items || []).filter((a) => a.kind.startsWith("PERSONAL"))));
  }, [refreshKey]);
  return (
    <div className="space-y-2" data-testid="sc-sensitive">
      <div className="text-[11px] text-slate-500">Personal Finance & Personal Continuity access in the last 24h (always audited, never inferred from role).</div>
      {items.length === 0 && <div className="text-[11px] text-slate-400 border border-dashed rounded-lg p-6 text-center">No sensitive-scope access in the last 24 hours.</div>}
      {items.map((a, i) => (
        <div key={i} className="flex items-center gap-2 text-[11.5px] border border-slate-200 bg-white rounded-lg px-3 py-2">
          <Pill v={a.severity} /><span>{a.label}</span></div>))}
    </div>);
}

function EventsTab({ refreshKey, auditMode }) {
  const [items, setItems] = useState([]);
  const [kinds, setKinds] = useState([]);
  const [kind, setKind] = useState("");
  useEffect(() => {
    api.get("/security-centre/events", { params: kind ? { kind } : {} })
      .then(({ data }) => { setItems(data.items || []); setKinds(data.kinds || []); });
  }, [refreshKey, kind]);
  return (
    <div className="space-y-2" data-testid={auditMode ? "sc-audit" : "sc-events"}>
      <select value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls}
          data-testid="sc-event-filter">
        <option value="">All events</option>
        {kinds.map((x) => <option key={x} value={x}>{x.replace(/_/g, " ")}</option>)}
      </select>
      <Tbl cols={["When", "Event", "User", "Context", "Severity", "Outcome"]}
          rows={items.map((e) => [fmt(e.at), <b>{e.kind.replace(/_/g, " ")}</b>, e.user,
            e.context, <Pill v={e.severity} />, e.outcome])} />
    </div>);
}

function DownloadsTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/downloads").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  return <Tbl testid="sc-downloads" cols={["When", "Source", "Action", "User", "Document", "Entity", "Reason"]}
      rows={items.map((d) => [fmt(d.at), d.source, d.action?.replace(/_/g, " "), d.user,
        d.document, d.entity, d.reason])} />;
}

function IntegrationsTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/integrations").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  return (
    <div className="space-y-2" data-testid="sc-integrations">
      <div className="text-[11px] text-slate-500">Credentials are never displayed here.</div>
      <Tbl cols={["Integration", "Status", "Owner", "Scope", "Last activity"]}
          rows={items.map((i) => [<b>{i.integration}</b>, i.status, i.owner, i.scope, fmt(i.last_activity)])} />
    </div>);
}

function PoliciesTab({ refreshKey, bump, run }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/security-centre/policies").then(({ data }) => setD(data)); }, [refreshKey]);
  if (!d) return null;
  const p = d.policies;
  const save = (patch) => run(async () => {
    await api.put("/security-centre/policies", patch);
    toast.success("Security policy updated"); bump();
  });
  const NumRow = ({ label, k }) => (
    <div className="flex items-center gap-2 text-[11.5px]">
      <span className="flex-1">{label}</span>
      <input type="number" defaultValue={p[k]} data-testid={`sc-pol-${k}`}
          onBlur={(e) => Number(e.target.value) !== p[k] && save({ [k]: Number(e.target.value) })}
          className={inputCls + " w-24"} /></div>);
  return (
    <div className="space-y-4 max-w-2xl" data-testid="sc-policies">
      <div className="border border-slate-200 rounded-xl bg-white p-4 space-y-2">
        <div className="text-[11px] font-semibold">Session policy</div>
        <NumRow label="Idle timeout (minutes)" k="idle_timeout_min" />
        <NumRow label="Absolute session lifetime (hours)" k="absolute_session_hours" />
        <NumRow label="Step-up re-verification interval (minutes)" k="stepup_interval_min" />
        <NumRow label="Dormant user period (days)" k="dormant_days" />
      </div>
      <div className="border border-slate-200 rounded-xl bg-white p-4">
        <div className="text-[11px] font-semibold mb-2">Step-up authentication areas</div>
        <div className="grid grid-cols-2 gap-1.5">
          {d.step_up_areas.map((a) => (
            <label key={a} className="text-[10.5px] inline-flex items-center gap-1.5">
              <input type="checkbox" checked={!!p.stepup_areas[a]} data-testid={`sc-stepup-${a}`}
                  onChange={(e) => save({ stepup_areas: { ...p.stepup_areas, [a]: e.target.checked } })} />
              {a.replace(/_/g, " ")}</label>))}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white p-4">
        <div className="text-[11px] font-semibold mb-2">Download reason required for sensitivities</div>
        <div className="flex flex-wrap gap-2">
          {["OWNER_ONLY", "PERSONAL_PRIVATE", "FINANCE_RESTRICTED", "CORPORATE_CONFIDENTIAL"].map((s) => (
            <label key={s} className="text-[10.5px] inline-flex items-center gap-1.5">
              <input type="checkbox" checked={p.download_reason_sensitivities.includes(s)}
                  data-testid={`sc-reason-${s}`}
                  onChange={(e) => save({ download_reason_sensitivities:
                    e.target.checked ? [...p.download_reason_sensitivities, s]
                    : p.download_reason_sensitivities.filter((x) => x !== s) })} />
              {s.replace(/_/g, " ")}</label>))}
        </div>
      </div>
      <div className="border border-slate-200 rounded-xl bg-white p-4">
        <div className="text-[11px] font-semibold mb-1">SYSTEM PROTECTED (read-only)</div>
        <div className="flex flex-wrap gap-1.5">
          {d.system_protected.map((s) => (
            <span key={s} className="text-[9.5px] px-2 py-1 rounded-full bg-slate-950 text-white">{s}</span>))}
        </div>
      </div>
    </div>);
}

function ReviewsTab({ refreshKey, bump }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/security-centre/access-reviews").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  const start = async () => {
    try { await api.post("/security-centre/access-reviews");
      toast.success("Access review cycle started"); bump();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const decide = async (rid, uid, decision) => {
    try { await api.post(`/security-centre/access-reviews/${rid}/items/${uid}`, { decision });
      toast.success(decision); bump();
      const { data } = await api.get("/security-centre/access-reviews");
      setItems(data.items || []);
      setOpen(data.items.find((r) => r.id === rid));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-3" data-testid="sc-reviews">
      <button onClick={start} data-testid="sc-start-review"
          className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-950 text-white">Start access review cycle</button>
      <Tbl cols={["Period", "Frequency", "Status", "Decided", ""]}
          rows={items.map((r) => [r.period, r.frequency,
            <Pill v={r.status} map={{ OPEN: "bg-amber-100 text-amber-800", COMPLETE: "bg-emerald-100 text-emerald-800" }} />,
            `${r.items.filter((i) => i.decision).length}/${r.items.length}`,
            <button onClick={() => setOpen(r)} data-testid={`sc-open-review-${r.id}`}
                className="text-[9.5px] px-1.5 py-0.5 rounded border border-slate-300">Open</button>])} />
      {open && (
        <Modal title={`Access review — ${open.period}`} onClose={() => setOpen(null)} wide>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto" data-testid="sc-review-items">
            {open.items.map((i) => (
              <div key={i.user_id} className="flex items-center gap-2 text-[11px] border border-slate-100 rounded-lg px-3 py-1.5">
                <span className="flex-1"><b>{i.email}</b> · {i.role} · {i.customer_scope}</span>
                {i.decision ? <Pill v={i.decision} map={{ CONFIRM: "bg-emerald-100 text-emerald-800",
                    MODIFY: "bg-amber-100 text-amber-800", DEACTIVATE: "bg-rose-100 text-rose-800",
                    REVIEW_REQUIRED: "bg-orange-100 text-orange-800" }} />
                  : ["CONFIRM", "MODIFY", "DEACTIVATE", "REVIEW_REQUIRED"].map((dec) => (
                    <button key={dec} onClick={() => decide(open.id, i.user_id, dec)}
                        data-testid={`sc-decide-${dec}-${i.user_id}`}
                        className="text-[8.5px] px-1.5 py-0.5 rounded border border-slate-300">{dec.replace(/_/g, " ")}</button>))}
              </div>))}
          </div>
        </Modal>)}
    </div>);
}

function AlertsTab({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/security-centre/alerts").then(({ data }) => setItems(data.items || [])); }, [refreshKey]);
  return (
    <div className="space-y-1.5" data-testid="sc-alerts">
      {items.length === 0 && <div className="text-[11.5px] text-emerald-700 border border-emerald-200 bg-emerald-50 rounded-lg px-3 py-4">No security alerts — thresholds transparent, no opaque scores.</div>}
      {items.map((a, i) => (
        <div key={i} className="flex items-center gap-2 border border-slate-200 bg-white rounded-lg px-3 py-2" data-testid={`sc-alert-${i}`}>
          <Pill v={a.severity} />
          <span className="text-[8.5px] font-semibold px-1.5 py-0.5 rounded bg-slate-900 text-white">{a.kind.replace(/_/g, " ")}</span>
          <span className="text-[11.5px]">{a.label}</span></div>))}
    </div>);
}
